<mat-card class="block">
  <mat-card-content class="!pt-5">
    <div class="flex items-center gap-2 mb-1">
      <mat-icon>euro_symbol</mat-icon>
      <span class="font-medium">{{ 'waters.form.section.fees' | translate }}</span>
    </div>
    <p class="text-sm text-gray-500 dark:text-gray-400 mb-4">{{ 'waters.form.section.fees-sub' | translate }}</p>

    @if (isLoading() && rows().length === 0) {
      <div class="py-6 flex justify-center">
        <mat-spinner diameter="32"/>
      </div>
    } @else if (loadFailed()) {
      <!-- Distinct from the empty state below: the fees could not be read, which says nothing about whether
           this lake has markets. Nothing retries by itself, so the way out is offered here. -->
      <div class="py-2">
        <p class="text-gray-500 dark:text-gray-400">{{ 'waters.market-fees.load-failed-hint' | translate }}</p>
        <button mat-stroked-button type="button" class="mt-2" (click)="retryLoad()">
          {{ 'waters.market-fees.retry' | translate }}
        </button>
      </div>
    } @else if (rows().length === 0) {
      <p class="text-gray-500 dark:text-gray-400 py-2">{{ 'waters.market-fees.no-markets' | translate }}</p>
    } @else {
      @if (showErrors() && hasPartialRows()) {
        <p class="text-red-600 text-sm mb-3">{{ 'waters.market-fees.dog-pair-error' | translate }}</p>
      }
      @if (showErrors() && hasOutOfRangeRows()) {
        <p class="text-red-600 text-sm mb-3">{{ 'waters.market-fees.price-out-of-range' | translate }}</p>
      }

      <!-- Left-aligned like a form, not centred like the numeric season matrices, and top-aligned so the single
           booking input and the market label sit on the same line as the dog pair. -->
      <div class="overflow-x-auto">
        <table class="w-full min-w-[34rem] border-collapse">
          <thead>
          <tr class="bg-surface-container text-left font-semibold">
            <th class="border border-outline-variant p-2 whitespace-nowrap">
              {{ 'waters.market-fees.market' | translate }}
            </th>
            <th class="border border-outline-variant p-2">
              {{ 'waters.market-fees.dog-fee' | translate }}
            </th>
            <th class="border border-outline-variant p-2">
              {{ 'waters.market-fees.booking-fee' | translate }}
            </th>
          </tr>
          </thead>
          <tbody>
            @for (row of rows(); track row.siteId) {
              <!-- The dog fee is one column holding a purchase/sale pair, so a half-filled pair outlines both
                   of its inputs; an out-of-range amount is the whole pair's problem for the same reason. -->
              @let dogPair = showErrors() && row.group.hasError('dogPair');
              @let dogInvalid = dogPair || (showErrors() && isDogOutOfRange(row));
              @let bookingInvalid = showErrors() && row.group.controls.bookingFee.invalid;
              <tr [formGroup]="row.group">
                <!-- pt-4 nudges the market name onto the first input's line: the inputs carry their own internal
                     padding, so an evenly padded cell starts above them. -->
                <th
                  scope="row"
                  class="border border-outline-variant bg-surface-container px-2 pt-4 pb-2 text-left align-top font-semibold whitespace-nowrap">
              <span class="flex items-center gap-2">
                @if (row.flag) {
                  <img class="h-auto w-[22px] rounded-[2px] align-middle ring-1 ring-black/15" [src]="row.flag" alt=""/>
                }
                {{ row.country }}
              </span>
                </th>

                <td
                  class="border border-outline-variant p-2 align-top"
                  [class]="dogInvalid ? 'outline-2 -outline-offset-2 outline-red-600' : ''">
                  <!-- One column made of two: purchase beside sale, sharing the width evenly so the pair reads as
                       a unit and the note line below can span both. -->
                  <div class="flex gap-2">
                    <mat-form-field class="min-w-24 flex-1" subscriptSizing="dynamic" floatLabel="always">
                      <mat-label>{{ 'waters.market-fees.purchase' | translate }}</mat-label>
                      <span matTextPrefix>€&nbsp;</span>
                      <input
                        matInput
                        type="number"
                        min="0"
                        step="0.01"
                        inputmode="decimal"
                        formControlName="dogPurchasePrice"/>
                    </mat-form-field>
                    <mat-form-field class="min-w-24 flex-1" subscriptSizing="dynamic" floatLabel="always">
                      <mat-label>{{ 'waters.market-fees.sale' | translate }}</mat-label>
                      <span matTextPrefix>€&nbsp;</span>
                      <input
                        matInput
                        type="number"
                        min="0"
                        step="0.01"
                        inputmode="decimal"
                        formControlName="dogSellingPrice"/>
                    </mat-form-field>
                  </div>

                  <!-- One slot under a cell: the margin, an em-dash, or the reason the cell is rejected. -->
                  @if (dogPair) {
                    <div class="mt-1 text-right text-xs text-red-600">
                      {{ 'waters.market-fees.dog-pair-error-cell' | translate }}
                    </div>
                  } @else if (dogInvalid) {
                    <div class="mt-1 text-right text-xs text-red-600">
                      {{ 'waters.market-fees.price-out-of-range-cell' | translate }}
                    </div>
                  } @else {
                    @let m = margin(row);
                    <div class="mt-1 text-right text-xs text-on-surface-variant">
                      @if (m !== null) {
                        {{ m | number: '1.1-1' }}{{ 'waters.market-fees.margin-suffix' | translate }}
                      } @else {
                        &mdash;
                      }
                    </div>
                  }
                </td>

                <td
                  class="border border-outline-variant p-2 align-top"
                  [class]="bookingInvalid ? 'outline-2 -outline-offset-2 outline-red-600' : ''">
                  <mat-form-field class="w-full min-w-28" subscriptSizing="dynamic">
                    <span matTextPrefix>€&nbsp;</span>
                    <input matInput type="number" min="0" step="0.01" inputmode="decimal" formControlName="bookingFee"/>
                  </mat-form-field>
                  @if (bookingInvalid) {
                    <div class="mt-1 text-right text-xs text-red-600">
                      {{ 'waters.market-fees.price-out-of-range-cell' | translate }}
                    </div>
                  }
                </td>
              </tr>
            }
          </tbody>
        </table>
      </div>

      <p class="mt-3 text-xs text-on-surface-variant">{{ 'waters.market-fees.hint' | translate }}</p>
    }
  </mat-card-content>
</mat-card>
